import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw, Link2, Activity, Heart, Moon, Flame, X } from "lucide-react";

// WHOOP brand teal
const WHOOP = "#00E0A4";

const Metric = ({ icon: Icon, label, value, suffix, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-muted/60 border border-border p-3 text-center">
    <Icon className="h-4 w-4 mx-auto mb-1" style={{ color: WHOOP }} />
    <p className="font-mono text-xl font-bold leading-none">
      {value == null ? "—" : value}<span className="text-xs text-muted-foreground">{value == null ? "" : suffix}</span>
    </p>
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{label}</p>
  </div>
);

export const WhoopConnect = () => {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/whoop/summary");
      setSummary(data);
    } catch (e) { /* no data yet */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/whoop/status");
      setStatus(data);
      if (data.connected) await loadSummary();
    } catch (e) {
      setStatus({ connected: false, configured: false });
    } finally { setLoading(false); }
  }, [loadSummary]);

  useEffect(() => { load(); }, [load]);

  // Handle OAuth redirect result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const w = params.get("whoop");
    if (w === "connected") toast.success("WHOOP connected");
    if (w === "error") toast.error("Could not connect WHOOP");
    if (w) window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whoop/authorize", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start WHOOP connect");
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      await loadSummary();
      toast.success("WHOOP data refreshed");
      await load();
    } catch (e) { toast.error("Sync failed"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/whoop/disconnect");
      setSummary(null);
      toast.success("Disconnected from WHOOP");
      await load();
    } catch (e) { toast.error("Could not disconnect"); }
    finally { setBusy(false); }
  };

  if (loading) return null;

  // Hide entirely if WHOOP isn't configured on the server and user isn't connected
  if (!status?.connected && status && status.configured === false) return null;

  if (!status?.connected) {
    return (
      <Card data-testid="whoop-connect-card" className="rounded-2xl bg-card border-border p-4 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-sm flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: WHOOP }} /> Connect WHOOP
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Sync recovery, sleep &amp; strain from your WHOOP band.</p>
          </div>
          <Button data-testid="whoop-connect-button" onClick={connect} disabled={busy} className="text-black font-semibold flex-shrink-0" style={{ background: WHOOP }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" /> Connect</>}
          </Button>
        </div>
      </Card>
    );
  }

  const r = summary?.recovery || {};
  const s = summary?.sleep || {};
  const st = summary?.strain || {};

  return (
    <Card data-testid="whoop-connected-card" className="rounded-2xl bg-card border-border p-4 mb-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="font-semibold text-sm flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: WHOOP }} /> WHOOP
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button data-testid="whoop-sync-button" onClick={sync} disabled={busy} size="sm" className="bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Refresh</>}
          </Button>
          <button data-testid="whoop-disconnect-button" onClick={disconnect} disabled={busy} className="text-muted-foreground hover:text-destructive transition-colors" title="Disconnect WHOOP">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Metric testid="whoop-recovery" icon={Activity} label="Recovery" value={r.recovery_score} suffix="%" />
        <Metric testid="whoop-sleep" icon={Moon} label="Sleep" value={s.performance_pct} suffix="%" />
        <Metric testid="whoop-strain" icon={Flame} label="Day strain" value={st.day_strain != null ? Math.round(st.day_strain * 10) / 10 : null} suffix="" />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Metric testid="whoop-rhr" icon={Heart} label="Resting HR" value={r.resting_heart_rate} suffix=" bpm" />
        <Metric testid="whoop-hrv" icon={Activity} label="HRV" value={r.hrv_ms != null ? Math.round(r.hrv_ms) : null} suffix=" ms" />
      </div>
      {summary?.workouts?.length ? (
        <p className="text-[11px] text-muted-foreground mt-2 text-center">{summary.workouts.length} recent WHOOP workout{summary.workouts.length === 1 ? "" : "s"} synced</p>
      ) : null}
    </Card>
  );
};

export default WhoopConnect;
